<template>
  <div class="big-screen-layout" :class="`device-${deviceType}`">
    <!-- 动态加载设备特定的布局组件 -->
    <component 
      :is="currentLayoutComponent" 
      :key="deviceType"
      ref="currentLayout"
      :current-time="currentTime"
      :last-update-time="lastUpdateTime"
      :loading="shouldShowLoading"
      @pause-auto-refresh="stopAutoRefresh"
      @resume-auto-refresh="startAutoRefresh"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
// 导入设备特定的布局组件
import PCLayout from './PCLayout.vue'
import IPadLayout from './IPadLayout.vue'
import MobileLayout from './MobileLayout.vue'
import MobileSmallLayout from './MobileSmallLayout.vue'

export default {
  name: 'BigScreenLayout',
  components: {
    PCLayout,
    IPadLayout,
    MobileLayout,
    MobileSmallLayout
  },
  data() {
    return {
      currentTime: '',
      timeTimer: null,
      refreshTimer: null,
      deviceType: 'pc', // 设备类型：pc, ipad, mobile, mobile-small
      isInitialLoad: true // 标记是否为首次加载
    }
  },
  computed: {
    ...mapState(['loading', 'lastUpdateTime']),
    // 根据设备类型返回对应的布局组件
    currentLayoutComponent() {
      const componentMap = {
        'pc': 'PCLayout',
        'ipad': 'IPadLayout',
        'mobile': 'MobileLayout',
        'mobile-small': 'MobileSmallLayout'
      }
      return componentMap[this.deviceType] || 'PCLayout'
    },
    // 控制loading显示：仅在首次加载时显示
    shouldShowLoading() {
      return this.loading && this.isInitialLoad
    }
  },
  mounted() {
    this.detectDevice()
    this.applyDeviceStyles()
    this.initTime()
    this.initData()
    this.startAutoRefresh()
    
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // 清理定时器和事件监听
    if (this.timeTimer) {
      clearInterval(this.timeTimer)
    }
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions(['fetchAllData']),
    // 初始化时间显示
    initTime() {
      this.updateTime()
      this.timeTimer = setInterval(this.updateTime, 1000)
    },
    // 更新当前时间
    updateTime() {
      const now = new Date()
      this.currentTime = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    // 初始化数据
    async initData() {
      this.$store.commit('SET_LOADING', true)
      try {
        await this.fetchAllData()
        this.$store.commit('SET_LAST_UPDATE_TIME', new Date().toLocaleTimeString())
      } catch (error) {
        console.error('数据初始化失败:', error)
      } finally {
        this.$store.commit('SET_LOADING', false)
        // 首次加载完成后，标记为非首次加载
        this.isInitialLoad = false
      }
    },
    // 开始自动刷新
    startAutoRefresh() {
      // 防止重复启动多个定时器
      if (this.refreshTimer) return
      this.refreshTimer = setInterval(async () => {
        try {
          // console.log('开始自动刷新数据...')
          // 自动刷新时不显示loading，直接更新数据
          
          // 重置所有数据状态 （取消重置数据）
          // await this.$store.dispatch('resetAllData')
          
          // 重新获取所有数据
          await this.fetchAllData()
          this.$store.commit('SET_LAST_UPDATE_TIME', new Date().toLocaleTimeString())
          
          // console.log('自动刷新完成')
        } catch (error) {
          console.error('数据刷新失败:', error)
          // 显示错误提示
          this.$message && this.$message.error('数据刷新失败，请检查网络连接')
        }
      }, 5000)
    },
    // 停止自动刷新
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    // 设备检测
    detectDevice() {
      const userAgent = navigator.userAgent.toLowerCase()
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      
      // 检测是否为移动设备
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
      
      // 检测是否为iPad
      const isIpad = /ipad/i.test(userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      
      // 检测是否为iPhone或Android手机
      const isPhone = /iphone|android.*mobile/i.test(userAgent)
      
      // 根据屏幕尺寸和设备类型判断
      if (isPhone || (isMobile && screenWidth <= 768)) {
        // 移动端
        if (screenWidth <= 480) {
          this.deviceType = 'mobile-small'
        } else {
          this.deviceType = 'mobile'
        }
      } else if (isIpad || (screenWidth > 768 && screenWidth <= 1440)) {
        // iPad端或小屏PC
        // this.deviceType = 'ipad'
        this.deviceType = 'pc'
      } else {
        // PC端
        this.deviceType = 'pc'
      }
      
      console.log('设备检测结果:', {
        deviceType: this.deviceType,
        screenWidth,
        screenHeight,
        userAgent: userAgent.substring(0, 100) + '...'
      })
    },
    
    // 应用设备样式
    applyDeviceStyles() {
      const layoutElement = this.$el
      
      // 移除所有设备类
      layoutElement.classList.remove('device-pc', 'device-ipad', 'device-mobile', 'device-mobile-small')
      
      // 添加当前设备类
      layoutElement.classList.add(`device-${this.deviceType}`)
      
      // 检测横屏模式（仅移动端）
      if (this.deviceType.includes('mobile')) {
        const isLandscape = window.innerWidth > window.innerHeight
        if (isLandscape) {
          layoutElement.classList.add('landscape-mode')
        } else {
          layoutElement.classList.remove('landscape-mode')
        }
      }
      
      console.log('应用设备样式:', `device-${this.deviceType}`)
    },
    
    handleResize() {
      // 重新检测设备类型
      const oldDeviceType = this.deviceType
      this.detectDevice()
      
      // 如果设备类型发生变化，强制重新渲染
      if (oldDeviceType !== this.deviceType) {
        this.$forceUpdate()
      }
      
      this.applyDeviceStyles()
      
      // 处理窗口大小变化，触发图表重绘
      this.$nextTick(() => {
        // 通知当前布局组件的所有子组件窗口大小已改变
        const currentLayout = this.$refs.currentLayout || this.$children[0]
        if (currentLayout && currentLayout.$children) {
          currentLayout.$children.forEach(child => {
            if (child.chart && typeof child.chart.resize === 'function') {
              child.chart.resize()
            }
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.big-screen-layout {
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0c1426 0%, #1a2332 50%, #0c1426 100%);
  color: #ffffff;
  font-family: 'Microsoft YaHei', Arial, sans-serif;
  position: relative;
  
  // 设备类型标识样式
  &.device-pc {
    overflow: hidden;
  }
  
  &.device-ipad {
    overflow-y: auto;
  }
  
  &.device-mobile,
  &.device-mobile-small {
    overflow-y: auto;
  }

  // 头部样式
  .screen-header {
    height: 80px;
    background: linear-gradient(90deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%);
    border-bottom: 2px solid rgba(24, 144, 255, 0.3);
    display: flex;
    align-items: center;
    padding: 0 40px;
    flex-shrink: 0; // 防止头部被压缩

    .header-content {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .logo-section {
        display: flex;
        align-items: center;
        gap: 20px;

        .logo {
          width: 50px;
          height: 50px;
          object-fit: contain;
        }

        .title {
          font-size: 32px;
          font-weight: bold;
          background: linear-gradient(45deg, #1890ff, #52c41a);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          text-shadow: 0 0 20px rgba(24, 144, 255, 0.5);
          white-space: nowrap;
        }
      }

      .time-section {
        text-align: right;

        .current-time {
          font-size: 24px;
          font-weight: bold;
          color: #1890ff;
          margin-bottom: 5px;
          white-space: nowrap;
        }

        .last-update {
          font-size: 14px;
          color: #8c8c8c;
          white-space: nowrap;
        }
      }
    }
  }

  // 主要内容区域
  .screen-main {
    min-height: calc(100vh - 80px);
    display: flex;
    gap: 20px;
    padding: 20px;

    // 左侧、右侧区域
    .left-section,
    .right-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-width: 0; // 防止flex子项溢出
    }

    // 中央区域
    .center-section {
      flex: 2;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-width: 0; // 防止flex子项溢出

      .map-container {
        flex: 2;
      }
    }

    // 模块容器
    .module-container {
      background: linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%);
      border: 1px solid rgba(24, 144, 255, 0.3);
      border-radius: 8px;
      padding: 20px;
      flex: 1;
      position: relative;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      min-height: 300px; // 增加最小高度
      height: 300px; // 设置固定高度
      display: flex;
      flex-direction: column;

      &.large {
        flex: 0 0 120px;
        height: 120px;
        min-height: 120px;
      }

      &.map-container {
        height: 400px;
        min-height: 400px;
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 8px;
        background: linear-gradient(45deg, transparent, rgba(24, 144, 255, 0.1), transparent);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover::before {
        opacity: 1;
      }
    }
  }

  // 加载遮罩
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(12, 20, 38, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;

    .loading-spinner {
      text-align: center;

      .spinner {
        width: 60px;
        height: 60px;
        border: 4px solid rgba(24, 144, 255, 0.3);
        border-top: 4px solid #1890ff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
      }

      .loading-text {
        font-size: 18px;
        color: #1890ff;
      }
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 响应式设计 - PC端大屏
@media (min-width: 1921px) {
  .big-screen-layout {
    .screen-header {
      height: 100px;
      padding: 0 60px;

      .header-content {
        .logo-section {
          gap: 30px;

          .logo {
            width: 60px;
            height: 60px;
          }

          .title {
            font-size: 36px;
          }
        }

        .time-section {
          .current-time {
            font-size: 28px;
          }

          .last-update {
            font-size: 16px;
          }
        }
      }
    }

    .screen-main {
      height: calc(100vh - 100px);
      padding: 30px;
      gap: 30px;

      .left-section,
      .right-section,
      .center-section {
        gap: 30px;
      }

      .module-container {
        padding: 30px;
        min-height: 250px;

        &.large {
          flex: 0 0 150px;
        }
      }
    }
  }
}

// 设备特定样式类 - PC端
.big-screen-layout.device-pc {
  .screen-header {
    .header-content {
      .logo-section {
        .title {
          font-size: 28px;
        }
      }
      .time-section {
        .current-time {
          font-size: 20px;
        }
      }
    }
  }
}

// 设备特定样式类 - iPad端
.big-screen-layout.device-ipad {
  .screen-header {
    height: 70px;
    padding: 0 30px;

    .header-content {
      .logo-section {
        gap: 15px;

        .logo {
          width: 40px;
          height: 40px;
        }

        .title {
          font-size: 24px;
        }
      }

      .time-section {
        .current-time {
          font-size: 18px;
        }

        .last-update {
          font-size: 12px;
        }
      }
    }
  }

  .screen-main {
    height: calc(100vh - 70px);
    padding: 15px;
    gap: 15px;
    flex-direction: column;

    // iPad端改为单栏布局
    .left-section,
    .center-section,
    .right-section {
      flex: none;
      width: 100%;
      flex-direction: row;
      gap: 15px;
      overflow-x: auto;
      
      .module-container {
        flex: 1;
        min-width: 300px;
        min-height: 180px;
      }
    }

    .center-section {
      .module-container {
        &.large {
          flex: 0 0 300px;
          min-height: 100px;
        }

        &.map-container {
          flex: 2;
          min-width: 400px;
          min-height: 300px;
        }
      }
    }

    .module-container {
      padding: 15px;
    }
  }
}

// 设备特定样式类 - 移动端
.big-screen-layout.device-mobile {
  overflow-y: auto;
  height: auto;
  min-height: 100vh;

  .screen-header {
    height: 60px;
    padding: 0 20px;
    flex-shrink: 0;

    .header-content {
      .logo-section {
        gap: 10px;

        .logo {
          width: 30px;
          height: 30px;
        }

        .title {
          font-size: 18px;
        }
      }

      .time-section {
        .current-time {
          font-size: 14px;
        }

        .last-update {
          font-size: 10px;
        }
      }
    }
  }

  .screen-main {
    height: auto;
    min-height: calc(100vh - 60px);
    padding: 10px;
    gap: 10px;
    flex-direction: column;

    // 移动端单栏垂直布局
    .left-section,
    .center-section,
    .right-section {
      flex: none;
      width: 100%;
      flex-direction: column;
      gap: 10px;
    }

    .module-container {
      padding: 15px;
      min-height: 250px;
      height: auto; // 移动端允许自适应高度
      
      &.large {
        flex: none;
        min-height: 120px;
        height: auto;
      }

      &.map-container {
        min-height: 300px;
        height: auto;
      }
    }
  }
}

// 设备特定样式类 - 超小屏幕移动端
.big-screen-layout.device-mobile-small {
  .screen-header {
    height: 50px;
    padding: 0 15px;

    .header-content {
      .logo-section {
        gap: 8px;

        .logo {
          width: 25px;
          height: 25px;
        }

        .title {
          font-size: 16px;
        }
      }

      .time-section {
        .current-time {
          font-size: 12px;
        }

        .last-update {
          font-size: 9px;
        }
      }
    }
  }

  .screen-main {
    min-height: calc(100vh - 50px);
    padding: 8px;
    gap: 8px;

    .module-container {
      padding: 12px;
      min-height: 200px;
      height: auto; // 超小屏幕允许自适应高度
      
      &.large {
        min-height: 100px;
        height: auto;
      }

      &.map-container {
        min-height: 250px;
        height: auto;
      }
    }
  }
}

// 横屏模式优化
.big-screen-layout.device-mobile.landscape-mode {
  .screen-main {
    .left-section,
    .center-section,
    .right-section {
      flex-direction: row;
      overflow-x: auto;
      
      .module-container {
        flex: 1;
        min-width: 250px;
        min-height: 150px;
      }
    }

    .center-section {
      .module-container {
        &.map-container {
          min-height: 200px;
        }
      }
    }
  }
}
</style>